/**
 * Privacy Policy Page
 * Static legal page. UK GDPR-oriented: what we collect, why, who processes it,
 * how long we keep it, and your rights.
 */

import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => (
  <div className="container legal-page" style={{ maxWidth: 720 }}>
    <h1>Privacy Policy</h1>
    <p><em>Effective date: 2 July 2026</em></p>

    <p>
      WriteFlow is a UK-based creative writing platform. This policy explains what
      personal data we collect, why, and the rights you have over it.
    </p>

    <h2>What we collect</h2>
    <ul>
      <li><strong>Account data</strong> — username, email address and a securely hashed password, plus any optional profile details you add (name, bio, avatar, location, website, pronouns).</li>
      <li><strong>Your content</strong> — posts, comments, likes, reactions, follows, bookmarks and reports you make.</li>
      <li><strong>Technical data</strong> — IP address and basic request logs, used for security and rate limiting.</li>
      <li><strong>Usage analytics</strong> — we use privacy-friendly product analytics (PostHog) to understand how features are used: anonymous-by-default product events (e.g. "a post was created"). Once you log in, events are linked to your account so we can understand and improve how WriteFlow is used.</li>
    </ul>

    <h2>Why we process it (lawful bases)</h2>
    <ul>
      <li><strong>To provide the service</strong> (contract) — accounts, posting, feeds, notifications.</li>
      <li><strong>To keep the service safe and improve it</strong> (legitimate interests) — security, moderation, analytics.</li>
      <li><strong>Service emails</strong> (legitimate interests, with opt-out) — the daily prompt reminder and streak reminder. Every email has a one-click unsubscribe, and you can manage both in Settings → Email preferences.</li>
    </ul>

    <h2>Who processes it for us</h2>
    <p>
      We use a small number of processors to run WriteFlow: Railway (hosting and
      database), PostHog (analytics) and Resend (email delivery). We do not sell
      your personal data to anyone.
    </p>

    <h2>Cookies and local storage</h2>
    <p>
      We store your login token in your browser's local storage (strictly necessary to
      keep you signed in). PostHog sets an analytics identifier so usage counts are
      accurate.
    </p>

    <h2>How long we keep it</h2>
    <p>
      For as long as you have an account. Deleting your account in Settings permanently
      removes your profile and posts from our database. Residual copies in backups and
      logs expire on their normal rotation.
    </p>

    <h2>Your rights</h2>
    <p>
      Under UK GDPR you can ask for access to, correction of, or deletion of your
      personal data, ask for a portable copy, and object to processing based on
      legitimate interests. Use the in-app feedback form to make a request. You also
      have the right to complain to the ICO (ico.org.uk).
    </p>

    <h2>Children</h2>
    <p>WriteFlow is not for children under 13, and we do not knowingly collect their data.</p>

    <h2>Changes</h2>
    <p>
      We may update this policy as WriteFlow evolves; material changes will be flagged
      on the site.
    </p>

    <p>
      See also our <Link to="/terms">Terms of Service</Link>.
    </p>
  </div>
);

export default PrivacyPolicyPage;
